// Automatic clock-change (DST) notice for the dashboard, derived from the
// resource's timezone; nothing is stored. Shaped like a notice from
// /market/notices/active so NoticesPanel renders both the same way.
import { getResourceTimezoneLabel, getTimezoneOffsetMinutes } from './datetime';

const DAY = 24 * 3600 * 1000;
const BRUSSELS = 'Europe/Brussels';
// Days before the switch day (local) at which the notice escalates.
export const REMINDER_DAYS = 7;
export const PINNED_DAYS = 2;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * First offset change in `tz` within [from, from + days), stepping `stepMs`
 * and narrowing to the minute. Null if none (e.g. UTC, or no DST that year).
 */
export const findNextDstTransition = (tz, from, days, stepMs = DAY) => {
  const end = from.getTime() + days * DAY;
  let t = from.getTime();
  let offset = getTimezoneOffsetMinutes(tz, new Date(t));
  while (t < end) {
    const next = Math.min(t + stepMs, end);
    const nextOffset = getTimezoneOffsetMinutes(tz, new Date(next));
    if (nextOffset !== offset) {
      let lo = t;
      let hi = next;
      while (hi - lo > 60000) {
        const mid = lo + Math.floor((hi - lo) / 2);
        if (getTimezoneOffsetMinutes(tz, new Date(mid)) === offset) lo = mid;
        else hi = mid;
      }
      // Switches fall on a whole minute T with lo < T <= hi < T + 1 min, so
      // flooring hi to the minute gives T exactly.
      return { at: new Date(hi - (hi % 60000)), before: offset, after: nextOffset };
    }
    t = next;
    offset = nextOffset;
  }
  return null;
};

const pad = (n) => String(n).padStart(2, '0');
// Wall clock of a UTC ms value shifted by `offset` minutes, read in UTC.
const wall = (ms, offset) => {
  const d = new Date(ms + offset * 60000);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};
const utcStamp = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC ${WEEKDAYS[d.getUTCDay()]}`;
};
const formatOffset = (minutes) => {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `UTC${sign}${Math.floor(abs / 60)}${abs % 60 ? `:${pad(abs % 60)}` : ''}`;
};
const formatShift = (minutes) => (Math.abs(minutes) === 60 ? 'one hour' : `${Math.abs(minutes)} minutes`);

/**
 * The dashboard notice for the next clock change in `tz`, shown from the
 * first day of the switch month (local time) until the end of the switch
 * day; null outside that window.
 */
export const buildDstNotice = (tz, now = new Date()) => {
  // Start a day back so the notice stays up for the rest of the switch day;
  // 33 days ahead reaches a switch on the 31st from the 1st of its month.
  const transition = findNextDstTransition(tz, new Date(now.getTime() - DAY), 33);
  if (!transition) return null;
  const { at, before, after } = transition;

  // Switch day in local time; midnights from the offsets either side.
  const local = new Date(at.getTime() + after * 60000);
  const [y, m, d] = [local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()];
  const dayStart = Date.UTC(y, m, d) - before * 60000;
  const dayEnd = Date.UTC(y, m, d + 1) - after * 60000;
  const monthStart = Date.UTC(y, m, 1) - before * 60000;
  if (now.getTime() >= dayEnd || now.getTime() < monthStart) return null;

  const nowLocal = new Date(now.getTime() + getTimezoneOffsetMinutes(tz, now) * 60000);
  const daysAway = Math.round(
    (Date.UTC(y, m, d) - Date.UTC(nowLocal.getUTCFullYear(), nowLocal.getUTCMonth(), nowLocal.getUTCDate())) / DAY
  );
  const when = daysAway <= 0 ? 'today' : daysAway === 1 ? 'tomorrow' : `in ${daysAway} days`;
  // Escalates as the day nears. The stage is part of the id, so dismissing
  // the heads-up does not hide the reminder; the last days are pinned.
  const stage = daysAway <= PINNED_DAYS ? 'pinned' : daysAway <= REMINDER_DAYS ? 'reminder' : 'heads-up';

  const back = after < before;
  const shift = after - before;
  const dayShort = `${d} ${MONTHS[m]}`;
  const dayLong = `${WEEKDAYS[local.getUTCDay()]} ${dayShort}`;
  const from = getResourceTimezoneLabel(tz, new Date(at.getTime() - 1));
  const to = getResourceTimezoneLabel(tz, at);
  const wallBefore = wall(at.getTime(), before);
  const wallAfter = wall(at.getTime(), after);
  const minutes = (dayEnd - dayStart) / 60000;
  const quarterHours = minutes / 15;
  const repeated = back
    ? `the ${wallAfter}-${wallBefore} hour happens twice`
    : `the ${wallBefore}-${wallAfter} hour is skipped`;
  // Gate hours are matched in Brussels time (generate_challenges_from_templates),
  // so only claim the UTC gate shift when Brussels switches at this instant.
  const gatesShift = getTimezoneOffsetMinutes(BRUSSELS, new Date(at.getTime() - 60000))
    !== getTimezoneOffsetMinutes(BRUSSELS, at);
  const gateMove = `${formatShift(shift)} ${back ? 'later' : 'earlier'} in UTC`;

  // Each line mirrors the API: day-ahead windows are the resource's local day
  // (compute_challenge_datetimes) at the 15-minute resolution submissions are
  // validated against; intraday windows are a fixed UTC span from the gate
  // (intraday_datetimes_from_gate), so the switch does not change them.
  return {
    id: `dst:${tz}:${at.toISOString()}:${stage}`,
    kind: 'dst',
    auto: true,
    stage,
    pinned: stage === 'pinned',
    title: `Clocks go ${back ? 'back' : 'forward'} ${when}, ${dayLong}`,
    summary: [
      `${from} → ${to}`,
      `day-ahead forecasts for ${dayShort} need ${quarterHours} quarter-hours`,
      ...(gatesShift ? [`gates ${gateMove}`] : []),
    ].join(' · '),
    details: [
      `${tz} moves from ${from} (${formatOffset(before)}) to ${to} (${formatOffset(after)}): ${wallBefore} becomes ${wallAfter}.`,
      `Day-ahead challenges (D+1 and later) for ${dayShort} cover the local day, ${utcStamp(dayStart)} → ${utcStamp(dayEnd - 15 * 60000)}: submit ${quarterHours} quarter-hours instead of 96, as ${repeated}.`,
      'Intraday challenges keep their fixed window in UTC, so their number of quarter-hours does not change.',
      ...(gatesShift
        ? [`Gate closures are scheduled in Brussels time, so from ${dayLong} they close ${gateMove}.`]
        : []),
    ].join('\n'),
    link_text: '',
    link_url: '',
    updated_at: at.toISOString(),
  };
};
